import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faFileAlt, faMoneyBillWave, faCoins,
  faPercent, faHandHoldingUsd, faChartLine, faChartPie, faChartBar,
  faBuilding, faFilter, faRotate, faDroplet,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import P2pReportPanel, { P2pReportSummary } from '../../components/P2pReportPanel';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

const THAI_DAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

interface MonthlyReportDay {
  date: string;
  total_orders: number;
  paid_orders: number;
  pending_orders: number;
  expired_orders: number;
  base_revenue: number;
  total_revenue: number;
  cent_difference: number;
  total_fee: number;
  net_revenue: number;
  success_rate: number;
  payout_count?: number;
  payout_amount?: number;
  payout_fee?: number;
}

interface ReportSummary {
  total_orders?: number;
  base_revenue?: number;
  total_fee?: number;
  net_revenue?: number;
  success_rate?: number;
  total_payouts?: number;
  total_payout_amount?: number;
  total_payout_fee?: number;
  total_p2p_fee?: number;
}

interface MerchantOption {
  id: string;
  name?: string;
  company_name?: string;
}

const toNumber = (value: unknown) => Number(value || 0);

const createLinePath = (points: Array<{ x: number; y: number }>) =>
  points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

const getSuccessRateTextColor = (rate: number) => {
  if (rate >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (rate >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-500 dark:text-red-400';
};

const getSuccessRateStrokeColor = (rate: number) => {
  if (rate >= 80) return '#10b981';
  if (rate >= 50) return '#f59e0b';
  return '#ef4444';
};

const getRevenueBandRange = (value: number, maxValue: number) => {
  if (maxValue <= 0) {
    return { min: 0, max: 0 };
  }

  const steps = 4;
  const bandSize = maxValue / steps;

  if (value <= 0) {
    return { min: 0, max: bandSize };
  }

  const bandIndex = Math.min(steps - 1, Math.floor((value - Number.EPSILON) / bandSize));

  return {
    min: bandIndex * bandSize,
    max: (bandIndex + 1) * bandSize,
  };
};

const MonthlyReportsPage: React.FC = () => {
  const now = dayjs().tz(TZ);
  const [draftYear, setDraftYear] = useState(now.year());
  const [draftMonth, setDraftMonth] = useState(now.month() + 1);
  const [draftMerchantId, setDraftMerchantId] = useState('');
  const [appliedYear, setAppliedYear] = useState(now.year());
  const [appliedMonth, setAppliedMonth] = useState(now.month() + 1);
  const [appliedMerchantId, setAppliedMerchantId] = useState('');
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [report, setReport] = useState<{ days: MonthlyReportDay[]; summary: ReportSummary }>({ days: [], summary: {} });
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRevenueTooltipIndex, setActiveRevenueTooltipIndex] = useState<number | null>(null);

  const loadReport = useCallback(async (
    y = appliedYear,
    m = appliedMonth,
    merchant = appliedMerchantId,
  ) => {
    try {
      setLoading(true);
      let url = `/admin/reports/monthly?year=${y}&month=${m}`;
      if (merchant) url += `&merchant_id=${merchant}`;
      const response = await merchantApi.get(url);
      setReport(response.data.data);
      if (response.data.data.merchants) {
        setMerchants(response.data.data.merchants);
      }
      // 💧 P2P เดือนนี้ (แยก) — ทั้งระบบตามเดือนที่เลือก
      const mFrom = dayjs().tz(TZ).year(y).month(m - 1).startOf('month').toISOString();
      const mTo = dayjs().tz(TZ).year(y).month(m - 1).endOf('month').toISOString();
      const p2pRes = await merchantApi.get('/admin/p2p-report', { params: { date_from: mFrom, date_to: mTo } }).catch(() => null);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch (error: any) {
      toast.error('ไม่สามารถโหลดรายงานได้: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [appliedYear, appliedMonth, appliedMerchantId]);

  useEffect(() => {
    loadReport();
  }, [appliedYear, appliedMonth, appliedMerchantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setAppliedYear(draftYear);
    setAppliedMonth(draftMonth);
    setAppliedMerchantId(draftMerchantId);
  };

  const handleReset = () => {
    const current = dayjs().tz(TZ);
    const y = current.year();
    const m = current.month() + 1;
    setDraftYear(y);
    setDraftMonth(m);
    setDraftMerchantId('');
    setAppliedYear(y);
    setAppliedMonth(m);
    setAppliedMerchantId('');
  };

  const formatCurrency = (val: number) =>
    `฿${toNumber(val).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`;
  const formatAxisCurrency = (val: number) =>
    `฿${toNumber(val).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`;
  const formatApproxRevenueRange = (value: number) => {
    const range = getRevenueBandRange(value, maxRevenueValue);
    return `${formatAxisCurrency(range.min)} - ${formatAxisCurrency(range.max)}`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const dayName = THAI_DAYS[d.getDay()];
    return `${dayName}. ${day}`;
  };

  const yearOptions = Array.from({ length: 3 }, (_, i) => now.year() - i);

  const columns: Column<MonthlyReportDay>[] = useMemo(() => [
    {
      key: 'date',
      header: 'วันที่',
      render: (d) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">
            {new Date(d.date).getDate()}
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{formatDate(d.date)}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{d.date}</p>
          </div>
        </div>
      )
    },
    {
      key: 'total_orders',
      header: 'Orders',
      sortable: true,
      render: (d) => <span className="font-bold text-gray-800 dark:text-slate-100">{d.total_orders}</span>
    },
    {
      key: 'paid_orders',
      header: 'Paid',
      sortable: true,
      render: (d) => (
        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
          {d.paid_orders}
        </span>
      )
    },
    {
      key: 'payin',
      header: 'Payin',
      sortable: true,
      render: (d) => (
        <div>
          {((d.paid_orders || 0) > 0 || toNumber((d as any).p2p_fee) > 0) ? (
            <>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{d.paid_orders} รายการ</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500">฿{toNumber(d.base_revenue).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-orange-500 dark:text-orange-400">Fee: ฿{toNumber(d.total_fee).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              {toNumber((d as any).p2p_fee) > 0 && (
                <div className="text-[11px] text-sky-500 dark:text-sky-400">P2P: ฿{toNumber((d as any).p2p_fee).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              )}
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'cent_difference',
      header: 'ส่วนต่าง',
      sortable: true,
      render: (d) => (
        <span className="font-semibold text-amber-600 dark:text-amber-400">
          {(d.cent_difference || 0) > 0 ? '+' : ''}฿{toNumber(d.cent_difference).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'payout_count',
      header: 'Payout',
      sortable: true,
      render: (d) => (
        <div>
          {(d.payout_count || 0) > 0 ? (
            <>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">{d.payout_count} รายการ</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500">฿{toNumber(d.payout_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-orange-500 dark:text-orange-400">Fee: ฿{toNumber(d.payout_fee).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'net_revenue',
      header: 'ยอดสุทธิ',
      sortable: true,
      render: (d) => (
        <span className="font-bold text-green-600 dark:text-green-400 text-base">
          {formatCurrency(d.net_revenue)}
        </span>
      )
    },
    {
      key: 'success_rate',
      header: 'Success %',
      sortable: true,
      render: (d) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${d.success_rate}%`,
                background: d.success_rate >= 80 ? 'linear-gradient(to right, #10b981, #34d399)' :
                  d.success_rate >= 50 ? 'linear-gradient(to right, #f59e0b, #fbbf24)' :
                    'linear-gradient(to right, #ef4444, #f87171)'
              }}
            ></div>
          </div>
          <span className={`text-xs font-bold min-w-[40px] text-right ${getSuccessRateTextColor(d.success_rate)}`}>
            {d.success_rate.toFixed(1)}%
          </span>
        </div>
      )
    }
  ], []);

  const summary = report.summary || {};
  const reportDays: MonthlyReportDay[] = (report.days || []).map((day: any) => ({
    date: day.date,
    total_orders: toNumber(day.total_orders),
    paid_orders: toNumber(day.paid_orders),
    pending_orders: toNumber(day.pending_orders),
    expired_orders: toNumber(day.expired_orders),
    base_revenue: toNumber(day.base_revenue),
    total_revenue: toNumber(day.total_revenue),
    cent_difference: toNumber(day.cent_difference),
    total_fee: toNumber(day.total_fee),
    net_revenue: toNumber(day.net_revenue),
    success_rate: toNumber(day.success_rate),
    payout_count: toNumber(day.payout_count),
    payout_amount: toNumber(day.payout_amount),
    payout_fee: toNumber(day.payout_fee),
  }));
  const activeDays = reportDays.filter((day) => day.total_orders > 0);
  const selectedMerchant = appliedMerchantId && merchants.length > 0
    ? merchants.find((m) => m.id === appliedMerchantId)
    : null;
  const selectedMerchantName = selectedMerchant?.name || selectedMerchant?.company_name || '';

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faFileAlt,
      iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
      label: 'Total Orders',
      value: <span className="text-gray-800 dark:text-slate-100">{(summary.total_orders || 0).toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ทั้งเดือน</span>,
    },
    {
      icon: faMoneyBillWave,
      iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
      label: 'ยอดฝาก (Base)',
      value: <span className="text-violet-600 dark:text-violet-400">{formatCurrency(summary.base_revenue || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดที่ลูกค้ากรอก</span>,
    },
    {
      icon: faPercent,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ค่าธรรมเนียม',
      value: <span className="text-red-600 dark:text-red-400">-฿{toNumber(summary.total_fee).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ค่าบริการรวม</span>,
    },
    {
      icon: faHandHoldingUsd,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'ยอดสุทธิ',
      value: <span className="text-emerald-600 dark:text-emerald-400">{formatCurrency(summary.net_revenue || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Merchants ได้</span>,
    },
    {
      icon: faChartLine,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Success Rate',
      value: <span className="text-green-600 dark:text-green-400">{toNumber(summary.success_rate).toFixed(1)}%</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">อัตราสำเร็จ</span>,
    },
  ], [summary]);

  const payoutStatCards: StatCardDef[] = useMemo(() => {
    if (!(summary.total_payouts || 0) && !(summary.total_payout_fee || 0) && !(summary.total_p2p_fee || 0)) return [];
    return [
      {
        icon: faCoins,
        iconClassName: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        label: 'Payout ทั้งเดือน',
        value: <span className="text-indigo-600 dark:text-indigo-400">{(summary.total_payouts || 0).toLocaleString()} รายการ</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดถอนรวม {formatCurrency(summary.total_payout_amount || 0)}</span>,
      },
      {
        icon: faPercent,
        iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
        label: 'ค่าธรรมเนียมถอน',
        value: <span className="text-orange-600 dark:text-orange-400">฿{toNumber(summary.total_payout_fee).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Payout Fee รวม</span>,
      },
      {
        icon: faDroplet,
        iconClassName: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        label: 'ค่าธรรมเนียม P2P',
        value: <span className="text-sky-600 dark:text-sky-400">฿{toNumber(summary.total_p2p_fee || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">หักจากกระเป๋า fee (P2P จริง)</span>,
      },
      {
        icon: faChartPie,
        iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
        label: 'Fee รวม (Payin + Payout + P2P)',
        value: <span className="text-purple-600 dark:text-purple-400">฿{toNumber((summary.total_fee || 0) + (summary.total_payout_fee || 0) + (summary.total_p2p_fee || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            Payin ฿{toNumber(summary.total_fee).toLocaleString('th-TH', { minimumFractionDigits: 2 })} + Payout ฿{toNumber(summary.total_payout_fee).toLocaleString('th-TH', { minimumFractionDigits: 2 })} + P2P ฿{toNumber(summary.total_p2p_fee || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
    ];
  }, [summary]);

  const maxRevenueValue = Math.max(1, ...reportDays.map((day) => Math.max(day.total_revenue, day.net_revenue)));
  const peakRevenueDay = reportDays.reduce<MonthlyReportDay | null>(
    (peak, day) => (!peak || day.total_revenue > peak.total_revenue ? day : peak),
    null
  );
  const highestOrdersDay = activeDays.reduce<MonthlyReportDay | null>(
    (peak, day) => (!peak || day.total_orders > peak.total_orders ? day : peak),
    null
  );
  const bestSuccessDay = activeDays.reduce<MonthlyReportDay | null>(
    (best, day) => (!best || day.success_rate > best.success_rate ? day : best),
    null
  );
  const weakestSuccessDay = activeDays.reduce<MonthlyReportDay | null>(
    (worst, day) => (!worst || day.success_rate < worst.success_rate ? day : worst),
    null
  );
  const averageNetRevenue = reportDays.length > 0
    ? reportDays.reduce((sum, day) => sum + day.net_revenue, 0) / reportDays.length
    : 0;
  const averageOrdersPerActiveDay = activeDays.length > 0
    ? activeDays.reduce((sum, day) => sum + day.total_orders, 0) / activeDays.length
    : 0;

  const orderStatusItems = [
    {
      label: 'Paid',
      value: reportDays.reduce((sum, day) => sum + day.paid_orders, 0),
      barClass: 'bg-green-500',
      textClass: 'text-green-600 dark:text-green-400',
      bgClass: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      label: 'Other',
      value: reportDays.reduce((sum, day) => sum + (day.total_orders - day.paid_orders), 0),
      barClass: 'bg-gray-400 dark:bg-slate-500',
      textClass: 'text-gray-600 dark:text-slate-300',
      bgClass: 'bg-gray-50 dark:bg-slate-700/50',
    },
  ];
  const totalStatusOrders = Math.max(1, orderStatusItems.reduce((sum, item) => sum + item.value, 0));
  const revenueAxisTicks = [1, 0.75, 0.5, 0.25, 0].map((ratio) => ({
    ratio,
    value: maxRevenueValue * ratio,
  }));
  const activeRevenueTooltipDay = activeRevenueTooltipIndex !== null
    ? reportDays[activeRevenueTooltipIndex] || null
    : null;
  const revenueTooltipLeftPercent = activeRevenueTooltipIndex !== null && reportDays.length > 0
    ? Math.min(88, Math.max(12, ((activeRevenueTooltipIndex + 0.5) / reportDays.length) * 100))
    : 50;

  const successChartWidth = Math.max(620, reportDays.length * 42);
  const successChartHeight = 220;
  const successChartPadding = { top: 18, right: 18, bottom: 30, left: 18 };
  const successChartBottom = successChartHeight - successChartPadding.bottom;
  const successChartInnerWidth = successChartWidth - successChartPadding.left - successChartPadding.right;
  const successChartInnerHeight = successChartHeight - successChartPadding.top - successChartPadding.bottom;
  const successPoints = reportDays.map((day, index) => {
    const x = successChartPadding.left + (
      reportDays.length === 1
        ? successChartInnerWidth / 2
        : (index / Math.max(reportDays.length - 1, 1)) * successChartInnerWidth
    );
    const y = successChartPadding.top + (1 - (day.success_rate / 100)) * successChartInnerHeight;

    return { x, y, day };
  });
  const successLinePath = successPoints.length > 0 ? createLinePath(successPoints) : '';
  const successAreaPath = successPoints.length > 1
    ? `${successLinePath} L ${successPoints[successPoints.length - 1].x} ${successChartBottom} L ${successPoints[0].x} ${successChartBottom} Z`
    : '';
  const successTicks = [0, 25, 50, 75, 100];
  const successLabelStep = reportDays.length > 20 ? 3 : 2;

  return (
    <div className="max-w-full">
      <UiPageHeader
        title="Monthly Reports"
        subtitle={`รายงานรายเดือน สรุปแต่ละวัน — ${THAI_MONTHS[appliedMonth - 1]} ${appliedYear}`}
        icon={faCalendarAlt}
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        actions={
          <button
            type="button"
            onClick={() => loadReport()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        }
      >
        <div className="flex flex-wrap items-end gap-4 w-fit">
          <div className="w-[160px] shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">เดือน</label>
            <select
              value={draftMonth}
              onChange={(e) => setDraftMonth(parseInt(e.target.value, 10))}
              className={FILTER_SELECT_CLASS}
            >
              {THAI_MONTHS.map((name, i) => (
                <option key={i} value={i + 1}>{name}</option>
              ))}
            </select>
          </div>
          <div className="w-[120px] shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ปี</label>
            <select
              value={draftYear}
              onChange={(e) => setDraftYear(parseInt(e.target.value, 10))}
              className={FILTER_SELECT_CLASS}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="w-[240px] shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
              <FontAwesomeIcon icon={faFilter} className="mr-1" />
              Merchant
            </label>
            <select
              value={draftMerchantId}
              onChange={(e) => setDraftMerchantId(e.target.value)}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">ทุก Merchant</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>{m.name || m.company_name}</option>
              ))}
            </select>
          </div>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={loading} cols={5} className="mb-6" />

      {payoutStatCards.length > 0 && (
        <UiStatsGrid cards={payoutStatCards} loading={loading} cols={4} className="mb-6" />
      )}

      <P2pReportPanel summary={p2p} title={`P2P — ${THAI_MONTHS[appliedMonth - 1]} ${appliedYear}`} />


      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <div className="xl:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-gray-50 dark:bg-slate-700/30">
            <div>
              <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-blue-500 text-xs" />
                กราฟยอดรับรายวัน
              </h2>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">เปรียบเทียบยอดรับจริงกับยอดสุทธิของแต่ละวันในเดือนนี้</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-blue-600 to-cyan-400"></span>
                ยอดรับจริง
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-green-600 to-emerald-400"></span>
                ยอดสุทธิ
              </span>
            </div>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="flex h-64 items-end gap-2 px-2">
                {Array.from({ length: 16 }).map((_, index) => (
                  <div key={index} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                    <div className="flex h-full items-end gap-1">
                      <div className="w-3 rounded-t-lg bg-gray-100 animate-pulse" style={{ height: `${30 + (index % 5) * 10}%` }}></div>
                      <div className="w-3 rounded-t-lg bg-gray-200 animate-pulse" style={{ height: `${22 + (index % 4) * 9}%` }}></div>
                    </div>
                    <div className="h-3 w-5 rounded bg-gray-100 animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : reportDays.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400 dark:text-slate-500">
                <FontAwesomeIcon icon={faChartLine} className="text-4xl text-gray-200 dark:text-slate-600 mb-3" />
                <p className="text-sm font-medium">ยังไม่มีข้อมูลกราฟของเดือนนี้</p>
                <p className="text-xs mt-1">ลองเปลี่ยนเดือนหรือเลือก Merchant อื่นดูได้</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto pb-2">
                  <div className="relative min-w-[840px] pt-24">
                    {activeRevenueTooltipDay && (
                      <div className="pointer-events-none absolute left-0 right-0 top-0 z-30 h-20">
                        <div
                          className="absolute min-w-[230px] max-w-[260px] -translate-x-1/2 rounded-xl border border-slate-200 bg-slate-900/95 px-3 py-2 text-xs text-white shadow-2xl backdrop-blur-sm"
                          style={{ left: `${revenueTooltipLeftPercent}%` }}
                        >
                          <div className="border-b border-white/10 pb-2">
                            <p className="font-semibold text-white">{formatDate(activeRevenueTooltipDay.date)}</p>
                            <p className="mt-0.5 text-[11px] text-slate-300">{activeRevenueTooltipDay.date}</p>
                          </div>

                          <div className="space-y-2 pt-2">
                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-1.5 text-slate-300">
                                  <span className="h-2.5 w-2.5 rounded-full bg-cyan-400"></span>
                                  ยอดรับจริง
                                </span>
                                <span className="font-semibold text-cyan-300">{formatCurrency(activeRevenueTooltipDay.total_revenue)}</span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-400">
                                ช่วงประมาณ: {formatApproxRevenueRange(activeRevenueTooltipDay.total_revenue)}
                              </p>
                            </div>

                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-1.5 text-slate-300">
                                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400"></span>
                                  ยอดสุทธิ
                                </span>
                                <span className="font-semibold text-emerald-300">{formatCurrency(activeRevenueTooltipDay.net_revenue)}</span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-400">
                                ช่วงประมาณ: {formatApproxRevenueRange(activeRevenueTooltipDay.net_revenue)}
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 border-t border-white/10 pt-2 text-[11px] text-slate-300">
                              <span>Orders: <strong className="text-white">{activeRevenueTooltipDay.total_orders.toLocaleString()}</strong></span>
                              <span>Success: <strong className="text-white">{activeRevenueTooltipDay.success_rate.toFixed(1)}%</strong></span>
                            </div>
                          </div>

                          <div className="absolute left-1/2 top-full h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-slate-200 bg-slate-900/95"></div>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3" onMouseLeave={() => setActiveRevenueTooltipIndex(null)}>
                      <div className="w-20 sm:w-24 flex-shrink-0">
                        <div className="flex h-56 flex-col justify-between pr-2 text-right text-[10px] font-medium text-gray-400 dark:text-slate-500">
                          {revenueAxisTicks.map((tick) => (
                            <span key={tick.ratio} className="whitespace-nowrap">
                              {formatAxisCurrency(tick.value)}
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 pr-2 text-right text-[10px] font-medium text-gray-300 dark:text-slate-600">ช่วงราคา</div>
                      </div>

                      <div className="flex-1">
                        <div className="relative h-56">
                          <div className="pointer-events-none absolute inset-0">
                            {revenueAxisTicks.map((tick) => (
                              <div
                                key={tick.ratio}
                                className="absolute left-0 right-0 border-t border-dashed border-gray-200 dark:border-slate-600"
                                style={{ top: `${(1 - tick.ratio) * 100}%` }}
                              ></div>
                            ))}
                          </div>

                          <div className="relative z-10 flex h-full items-end gap-2 px-1">
                            {reportDays.map((day, index) => {
                              const totalHeight = day.total_revenue > 0
                                ? Math.max((day.total_revenue / maxRevenueValue) * 100, 5)
                                : 0;
                              const netHeight = day.net_revenue > 0
                                ? Math.max((day.net_revenue / maxRevenueValue) * 100, 5)
                                : 0;
                              const isPeak = peakRevenueDay?.date === day.date && day.total_revenue > 0;

                              return (
                                <button
                                  key={day.date}
                                  type="button"
                                  onMouseEnter={() => setActiveRevenueTooltipIndex(index)}
                                  onFocus={() => setActiveRevenueTooltipIndex(index)}
                                  onClick={() => setActiveRevenueTooltipIndex((current) => current === index ? null : index)}
                                  className="relative flex h-full min-w-[18px] flex-1 items-end justify-center gap-1 bg-transparent focus:outline-none"
                                >
                                  <div
                                    className={`w-3 rounded-t-md bg-gradient-to-t from-blue-600 to-cyan-400 transition-all duration-300 hover:opacity-90 ${isPeak ? 'ring-2 ring-blue-200 ring-offset-1' : ''}`}
                                    style={{ height: `${totalHeight}%`, minHeight: day.total_revenue > 0 ? '6px' : '0px' }}
                                  ></div>
                                  <div
                                    className="w-3 rounded-t-md bg-gradient-to-t from-green-600 to-emerald-400 transition-all duration-300 hover:opacity-90"
                                    style={{ height: `${netHeight}%`, minHeight: day.net_revenue > 0 ? '6px' : '0px' }}
                                  ></div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="mt-2 flex gap-2 px-1">
                          {reportDays.map((day) => {
                            const isPeak = peakRevenueDay?.date === day.date && day.total_revenue > 0;

                            return (
                              <div key={day.date} className="min-w-[18px] flex-1 text-center">
                                <span className={`text-[10px] ${isPeak ? 'font-bold text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}>
                                  {new Date(day.date).getDate()}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
                    <p className="text-[11px] font-medium text-blue-500 dark:text-blue-400 uppercase">Peak Revenue</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                      {peakRevenueDay ? formatDate(peakRevenueDay.date) : '-'}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {peakRevenueDay ? formatCurrency(peakRevenueDay.total_revenue) : 'ไม่มีข้อมูล'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3">
                    <p className="text-[11px] font-medium text-emerald-500 dark:text-emerald-400 uppercase">Average Net / Day</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">{formatCurrency(averageNetRevenue)}</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                      เฉลี่ย {reportDays.length.toLocaleString()} วันในรายงาน
                    </p>
                  </div>
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
                    <p className="text-[11px] font-medium text-amber-500 dark:text-amber-400 uppercase">Average Orders / Active Day</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">{averageOrdersPerActiveDay.toFixed(1)} Orders</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      วันที่มีรายการ {activeDays.length.toLocaleString()} วัน
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/30">
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faChartPie} className="text-emerald-500 text-xs" />
              ภาพรวมสถานะ Orders
            </h2>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">สัดส่วน Paid, Pending และ Expired ทั้งเดือน</p>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="space-y-3">
                <div className="h-24 rounded-2xl bg-gray-100 animate-pulse"></div>
                <div className="h-3 rounded-full bg-gray-100 animate-pulse"></div>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-12 rounded-xl bg-gray-100 animate-pulse"></div>
                ))}
              </div>
            ) : reportDays.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400 dark:text-slate-500">
                <FontAwesomeIcon icon={faChartPie} className="text-4xl text-gray-200 dark:text-slate-600 mb-3" />
                <p className="text-sm font-medium">ไม่มีข้อมูลสำหรับสรุปสถานะ</p>
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-emerald-100 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20 p-4">
                  <p className="text-[11px] font-medium text-emerald-500 dark:text-emerald-400 uppercase">Monthly Success Rate</p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className={`text-3xl font-bold ${getSuccessRateTextColor(toNumber(summary.success_rate))}`}>
                      {toNumber(summary.success_rate).toFixed(1)}%
                    </span>
                    <span className="text-sm text-gray-400 dark:text-slate-500">เฉลี่ยทั้งเดือน</span>
                  </div>
                  {selectedMerchantName && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                      Merchant: <span className="font-medium text-gray-700 dark:text-slate-200">{selectedMerchantName}</span>
                    </p>
                  )}
                </div>

                <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700 flex">
                  {orderStatusItems.map((item) => (
                    <div
                      key={item.label}
                      className={`${item.barClass} transition-all duration-500`}
                      style={{ width: `${(item.value / totalStatusOrders) * 100}%` }}
                    ></div>
                  ))}
                </div>

                <div className="mt-4 space-y-2">
                  {orderStatusItems.map((item) => (
                    <div key={item.label} className={`flex items-center gap-2 rounded-xl px-3 py-2 ${item.bgClass}`}>
                      <span className={`h-3 w-3 rounded-full ${item.barClass}`}></span>
                      <span className="text-sm text-gray-600 dark:text-slate-300">{item.label}</span>
                      <span className={`ml-auto text-sm font-bold ${item.textClass}`}>
                        {item.value.toLocaleString()} ({((item.value / totalStatusOrders) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2">
                  <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-400 dark:text-slate-500">Highest Orders Day</p>
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mt-1">
                      {highestOrdersDay ? `${formatDate(highestOrdersDay.date)} • ${highestOrdersDay.total_orders.toLocaleString()} Orders` : 'ไม่มีข้อมูล'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-400 dark:text-slate-500">Days With Activity</p>
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mt-1">{activeDays.length.toLocaleString()} วัน</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-gray-50 dark:bg-slate-700/30">
          <div>
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faChartBar} className="text-amber-500 text-xs" />
              Success Rate Trend
            </h2>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">เส้นแนวโน้มอัตราสำเร็จรายวันของเดือนนี้</p>
          </div>
          <span className={`inline-flex self-start rounded-full px-3 py-1 text-xs font-semibold ${getSuccessRateTextColor(toNumber(summary.success_rate))} bg-white dark:bg-slate-700`}>
            Average {toNumber(summary.success_rate).toFixed(1)}%
          </span>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="space-y-4">
              <div className="h-60 rounded-2xl bg-gray-100 animate-pulse"></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-20 rounded-xl bg-gray-100 animate-pulse"></div>
                ))}
              </div>
            </div>
          ) : reportDays.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400 dark:text-slate-500">
              <FontAwesomeIcon icon={faChartLine} className="text-4xl text-gray-200 dark:text-slate-600 mb-3" />
              <p className="text-sm font-medium">ไม่มีข้อมูลสำหรับแสดงเส้นแนวโน้ม</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <svg
                  viewBox={`0 0 ${successChartWidth} ${successChartHeight}`}
                  className="h-60 w-full min-w-[620px]"
                  role="img"
                  aria-label="Monthly success rate trend"
                >
                  <defs>
                    <linearGradient id="monthlySuccessStroke" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#f59e0b" />
                      <stop offset="50%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                    <linearGradient id="monthlySuccessArea" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="rgba(16,185,129,0.18)" />
                      <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                    </linearGradient>
                  </defs>

                  {successTicks.map((tick) => {
                    const y = successChartPadding.top + (1 - (tick / 100)) * successChartInnerHeight;

                    return (
                      <g key={tick}>
                        <line
                          x1={successChartPadding.left}
                          y1={y}
                          x2={successChartWidth - successChartPadding.right}
                          y2={y}
                          stroke="#e5e7eb"
                          strokeDasharray="4 4"
                        />
                        <text x={0} y={y + 4} fontSize="10" fill="#94a3b8">{tick}%</text>
                      </g>
                    );
                  })}

                  {successAreaPath && (
                    <path d={successAreaPath} fill="url(#monthlySuccessArea)"></path>
                  )}

                  {successLinePath && (
                    <path
                      d={successLinePath}
                      fill="none"
                      stroke="url(#monthlySuccessStroke)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  )}

                  {successPoints.map((point, index) => (
                    <g key={point.day.date}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="4.5"
                        fill="white"
                        stroke={getSuccessRateStrokeColor(point.day.success_rate)}
                        strokeWidth="3"
                      >
                        <title>{`${formatDate(point.day.date)} • ${point.day.success_rate.toFixed(1)}% • Paid ${point.day.paid_orders}/${point.day.total_orders}`}</title>
                      </circle>

                      {(index % successLabelStep === 0 || index === successPoints.length - 1) && (
                        <text
                          x={point.x}
                          y={successChartBottom + 18}
                          fontSize="10"
                          fill="#94a3b8"
                          textAnchor="middle"
                        >
                          {new Date(point.day.date).getDate()}
                        </text>
                      )}
                    </g>
                  ))}
                </svg>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3">
                  <p className="text-[11px] uppercase text-emerald-500 dark:text-emerald-400 font-medium">Best Day</p>
                  <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                    {bestSuccessDay ? formatDate(bestSuccessDay.date) : 'ไม่มีข้อมูล'}
                  </p>
                  <p className={`text-xs mt-1 ${bestSuccessDay ? getSuccessRateTextColor(bestSuccessDay.success_rate) : 'text-gray-400 dark:text-slate-500'}`}>
                    {bestSuccessDay ? `${bestSuccessDay.success_rate.toFixed(1)}%` : '-'}
                  </p>
                </div>
                <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
                  <p className="text-[11px] uppercase text-amber-500 dark:text-amber-400 font-medium">Lowest Day</p>
                  <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                    {weakestSuccessDay ? formatDate(weakestSuccessDay.date) : 'ไม่มีข้อมูล'}
                  </p>
                  <p className={`text-xs mt-1 ${weakestSuccessDay ? getSuccessRateTextColor(weakestSuccessDay.success_rate) : 'text-gray-400 dark:text-slate-500'}`}>
                    {weakestSuccessDay ? `${weakestSuccessDay.success_rate.toFixed(1)}%` : '-'}
                  </p>
                </div>
                <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
                  <p className="text-[11px] uppercase text-blue-500 dark:text-blue-400 font-medium">Performance Snapshot</p>
                  <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                    {reportDays.length.toLocaleString()} วันในรายงาน
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Paid {orderStatusItems[0].value.toLocaleString()} จาก {totalStatusOrders.toLocaleString()} Orders
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Daily Report Table */}
      <DataTable
        data={reportDays}
        columns={columns}
        loading={loading}
        keyExtractor={(row) => row.date}
        emptyMessage="ไม่มีข้อมูลในเดือนนี้"
        emptyIcon={<FontAwesomeIcon icon={faChartPie} className="text-5xl text-gray-200 dark:text-slate-600" />}
        pagination={{ pageSize: 31, showPageSizeSelector: false }}
        headerContent={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-lg" style={{ color: 'var(--theme-primary)' }} />
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">
              รายงานรายวัน - {THAI_MONTHS[appliedMonth - 1]} {appliedYear}
              {selectedMerchantName && (
                <span className="text-sm font-normal text-gray-500 dark:text-slate-400 ml-0 sm:ml-2 inline-flex items-center">
                  <FontAwesomeIcon icon={faBuilding} className="mr-1" />
                  {selectedMerchantName}
                </span>
              )}
            </h2>
          </div>
        }
        hoverable
      />
    </div>
  );
};

export default MonthlyReportsPage;
